<script setup lang="ts">
/**
 * Shared player picker (Phase 16 Step 9; legacy G1/P4 — Displaro had several divergent player pickers).
 * Search (own box, or the parent's `query`), "N selected", Select all shown / Clear, status and tags per row, an
 * optional per-row note ("Already on this player", "also in Lobby"), 50 rows then "Show more".
 * Either filters the `players` it is given, or (`server`) pages them from the server (GET players-page).
 */
import { useI18n } from 'vue-i18n';
import { computed, onBeforeUnmount, ref, shallowRef, watch } from 'vue';
import type { ScreenSummary } from '@codot/contracts';
import { pagePlayers } from '../../api/screens.api';
import { PLAYER_STATUS, playerStatus } from '../../utils/player-status';

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    players?: ScreenSummary[];
    /** Page players from the server (large fleets) instead of filtering `players`. */
    server?: boolean;
    /** Filter text from the parent; when absent the picker shows its own search box. */
    query?: string;
    note?: (p: ScreenSummary) => string | null;
    /** Players left out of the list (e.g. already in the group being added to). */
    hide?: (p: ScreenSummary) => boolean;
    label?: string;
    maxHeight?: string;
  }>(),
  { players: () => [], server: false, query: undefined, note: undefined, hide: undefined, label: undefined, maxHeight: '14rem' },
);
const picked = defineModel<string[]>({ required: true });

const own = ref('');
const title = computed(() => props.label ?? t('publish.players'));
const q = computed(() => (props.query ?? own.value).trim().toLowerCase());

/* Server mode: search runs there (name, tags, group, device…); "Show more" asks for the next page. */
const serverRows = shallowRef<ScreenSummary[]>([]);
const serverTotal = ref(0);
const serverNext = ref<number | null>(null);
let generation = 0;
async function fetchServer(offset: number): Promise<void> {
  const gen = offset === 0 ? ++generation : generation;
  const page = await pagePlayers({ q: q.value, offset });
  if (gen !== generation) return;
  serverRows.value = offset ? [...serverRows.value, ...page.items] : page.items;
  serverTotal.value = page.total;
  serverNext.value = page.nextOffset;
}
let searchTimer: ReturnType<typeof setTimeout> | undefined;
if (props.server) {
  void fetchServer(0).catch(() => undefined);
  watch(q, () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => void fetchServer(0).catch(() => undefined), 250);
  });
}
onBeforeUnmount(() => clearTimeout(searchTimer));

const shown = computed(() =>
  (props.server ? serverRows.value : props.players.filter((p) => !q.value || [p.name, ...p.tags].some((v) => v.toLowerCase().includes(q.value)))).filter(
    (p) => !props.hide?.(p),
  ),
);
const PAGE = 50;
const limit = ref(PAGE);
watch(q, () => (limit.value = PAGE));
const page = computed(() => (props.server ? shown.value : shown.value.slice(0, limit.value)));
/** Rows not shown yet. */
const left = computed(() => Math.max(0, props.server ? serverTotal.value - serverRows.value.length : shown.value.length - page.value.length));
let loadingMore = false;
async function showMore(): Promise<void> {
  if (!props.server) {
    limit.value += PAGE;
    return;
  }
  if (loadingMore || serverNext.value === null) return;
  loadingMore = true;
  await fetchServer(serverNext.value).catch(() => undefined);
  loadingMore = false;
}

function toggle(id: string): void {
  picked.value = picked.value.includes(id) ? picked.value.filter((x) => x !== id) : [...picked.value, id];
}
const allShownPicked = computed(() => shown.value.length > 0 && shown.value.every((p) => picked.value.includes(p.id)));
function toggleAllShown(): void {
  const ids = new Set(shown.value.map((p) => p.id));
  picked.value = allShownPicked.value ? picked.value.filter((x) => !ids.has(x)) : [...new Set([...picked.value, ...ids])];
}
</script>

<template>
  <div class="space-y-1.5">
    <input v-if="query === undefined" v-model="own" type="search" class="input py-1.5" :placeholder="t('publish.searchThing', { thing: title.toLowerCase() })" :aria-label="t('publish.searchThing', { thing: title.toLowerCase() })" />
    <div class="flex items-center gap-3 text-xs text-slate-500">
      <span class="font-medium tracking-wider uppercase">{{ title }}</span>
      <button v-if="shown.length" type="button" class="text-indigo-600 hover:underline dark:text-indigo-400" @click="toggleAllShown">
        {{ allShownPicked ? t('publish.clearShown') : t('publish.selectAllShown', { n: shown.length }) }}
      </button>
      <span class="ms-auto" aria-live="polite">{{ t('publish.nSelected', { n: picked.length }) }}</span>
    </div>
    <ul
      class="divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200 text-sm dark:divide-slate-800 dark:border-slate-800"
      :style="{ maxHeight }"
    >
      <li v-for="p in page" :key="p.id">
        <label class="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/50">
          <input type="checkbox" class="h-4 w-4 accent-indigo-600" :checked="picked.includes(p.id)" @change="toggle(p.id)" />
          <span class="h-2 w-2 shrink-0 rounded-full" :class="PLAYER_STATUS[playerStatus(p)].dot" :title="t(PLAYER_STATUS[playerStatus(p)].key)" aria-hidden="true" />
          <span class="min-w-0 flex-1 truncate">{{ p.name }}</span>
          <span class="shrink-0 text-[11px] text-slate-500">{{ t(PLAYER_STATUS[playerStatus(p)].key) }}</span>
          <span v-for="tg in p.tags.slice(0, 2)" :key="tg" class="badge bg-slate-100 text-[10px] dark:bg-slate-800">{{ tg }}</span>
          <span v-if="note?.(p)" class="max-w-40 truncate text-[11px] text-slate-500" :title="note(p) ?? undefined">{{ note(p) }}</span>
        </label>
      </li>
      <li v-if="!shown.length" class="px-3 py-2 text-slate-500">{{ q ? t('publish.noPlayerMatch') : t('publish.noPlayers') }}</li>
      <li v-if="left > 0">
        <button type="button" class="w-full px-3 py-2 text-start text-sm text-indigo-600 hover:bg-slate-50 dark:text-indigo-400 dark:hover:bg-slate-800/50" @click="showMore">
          {{ t('publish.showMore', { n: left }) }}
        </button>
      </li>
    </ul>
  </div>
</template>
